<script setup lang="ts">
import { ref, watch } from 'vue';
import type { QualityPreset } from '../types';
import { QUALITY_PRESETS } from '../composables/useWebRTC';
import { useDesktopCompanion, type DesktopSource } from '../composables/useDesktopCompanion';

const props = defineProps<{
  isStreaming: boolean;
  currentStreamer?: { id: string; name: string; isStreaming: boolean; isSelf?: boolean } | null;
  isInsideDiscord: boolean;
  selectedQuality: QualityPreset;
  channelId?: string;
  userName?: string;
}>();

const emit = defineEmits<{
  (e: 'start-screen'): void;
  (e: 'open-browser'): void;
  (e: 'stop-stream'): void;
  (e: 'update:quality', quality: QualityPreset): void;
}>();

const {
  isCompanionOnline,
  isCompanionStreaming,
  isFetchingSources,
  sources,
  fetchSources,
  startDesktopStream,
  stopDesktopStream,
} = useDesktopCompanion();

const isSomeoneElseStreaming = ref(false);
watch(() => props.currentStreamer, (cs: any) => {
  isSomeoneElseStreaming.value = !!cs?.isStreaming && !cs?.isSelf;
}, { immediate: true, deep: true });

const showSourceModal = ref(false);
const selectedSource = ref<DesktopSource | null>(null);

async function handleOpenModal() {
  if (isSomeoneElseStreaming.value) {
    alert(`⚠️ Transmissão Bloqueada:\n\n${props.currentStreamer?.name} já está transmitindo tela nesta chamada!\nApenas 1 pessoa pode transmitir por vez.`);
    return;
  }
  showSourceModal.value = true;
  if (isCompanionOnline.value) {
    const list = await fetchSources();
    if (list.length > 0 && !selectedSource.value) {
      const best = list.find(s => !s.isDiscord && !s.name.toLowerCase().includes('nexia companion') && !s.name.toLowerCase().includes('discord'));
      selectedSource.value = best || list[0];
    }
  }
}

async function handleStartDesktop() {
  const targetRoom = (props.channelId && props.channelId !== 'default-room')
    ? props.channelId
    : (typeof window !== 'undefined' ? (localStorage.getItem('nexia_room_id') || 'sala-principal') : 'sala-principal');

  const ok = await startDesktopStream({
    sourceId: 'screen:0:0',
    sourceName: 'Tela Completa',
    roomId: targetRoom,
    streamerName: props.userName || 'Streamer',
    quality: props.selectedQuality,
  });
  if (ok) {
    showSourceModal.value = false;
  }
}

async function handleStopAll() {
  if (isCompanionStreaming.value) {
    await stopDesktopStream();
  }
  emit('stop-stream');
}
</script>

<template>
  <div class="flex items-center gap-2">
    <!-- Se NÃO estiver transmitindo -->
    <template v-if="!isStreaming && !isCompanionStreaming">
      <!-- Aviso de Bloqueio se outra pessoa já estiver transmitindo -->
      <div
        v-if="isSomeoneElseStreaming"
        class="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-amber-500/15 border border-amber-500/30 text-amber-300"
        :title="`Apenas 1 pessoa pode transmitir por vez. ${currentStreamer?.name} está transmitindo no momento.`"
      >
        <span>🔒 {{ currentStreamer?.name }} está transmitindo</span>
      </div>

      <!-- Botão Transmitir Principal (Abre Modal de Seleção) -->
      <button
        v-else
        @click="handleOpenModal"
        class="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-[#5865F2] hover:bg-[#4752C4] shadow-sm transition active:scale-95 cursor-pointer"
        title="Transmitir sua tela ou jogo para a chamada"
      >
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
        <span>Transmitir Jogo / Tela</span>
      </button>

      <!-- Badge se o companion anti-eco estiver ativo -->
      <div
        v-if="isCompanionOnline && !isSomeoneElseStreaming"
        class="hidden sm:flex items-center gap-1 px-2 py-1 rounded bg-[#23a55a]/15 border border-[#23a55a]/30 text-[#23a55a] text-[11px] font-semibold"
        title="App Desktop detectado: o áudio do Discord será excluído automaticamente!"
      >
        <span class="w-1.5 h-1.5 rounded-full bg-[#23a55a] animate-pulse"></span>
        <span>Anti-Eco Pronto</span>
      </div>
    </template>

    <!-- Se ESTIVER transmitindo: Botão Encerrar Stream -->
    <button
      v-else
      @click="handleStopAll"
      class="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-[#f23f43] hover:bg-[#d83a3e] shadow-sm transition active:scale-95 cursor-pointer"
    >
      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
      <span>Parar Transmissão</span>
    </button>

    <!-- Seletor de Qualidade -->
    <select
      :value="selectedQuality.id"
      @change="(e) => {
        const val = (e.target as HTMLSelectElement).value;
        const found = QUALITY_PRESETS.find(q => q.id === val);
        if (found) emit('update:quality', found);
      }"
      class="bg-[#1e1f22] border border-white/10 hover:border-white/20 text-xs text-[#dbdee1] rounded-md px-2 py-1.5 outline-none cursor-pointer transition"
    >
      <option v-for="q in QUALITY_PRESETS" :key="q.id" :value="q.id" class="bg-[#1e1f22] text-[#dbdee1]">
        {{ q.name.split(' ')[0] }} ({{ q.fps ? q.fps + 'fps' : 'Auto' }})
      </option>
    </select>

    <!-- ── MODAL DE TRANSMISSÃO INTELIGENTE ── -->
    <div
      v-if="showSourceModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4 animate-fade-in"
      @click.self="showSourceModal = false"
    >
      <div class="bg-[#2b2d31] border border-white/10 rounded-2xl p-5 max-w-lg w-full shadow-2xl space-y-4">
        <!-- Header do Modal -->
        <div class="flex items-center justify-between pb-3 border-b border-white/10">
          <div class="flex items-center gap-2">
            <div class="w-7 h-7 rounded-lg bg-[#5865F2]/20 border border-[#5865F2]/40 flex items-center justify-center text-[#5865F2]">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 class="text-sm font-bold text-white">Transmitir Jogo ou Tela</h3>
              <p class="text-[11px] text-[#949ba4]">
                {{ isCompanionOnline ? 'Anti-Eco ativo: áudio do Discord excluído automaticamente' : 'Selecione como deseja transmitir' }}
              </p>
            </div>
          </div>
          <button
            @click="showSourceModal = false"
            class="text-[#949ba4] hover:text-white p-1 rounded-md hover:bg-white/10 transition cursor-pointer text-sm font-bold"
          >
            ✕
          </button>
        </div>

        <!-- CENÁRIO A: COMPANION ATIVO (Tela Completa Direto!) -->
        <div v-if="isCompanionOnline" class="space-y-4">
          <div class="p-4 rounded-xl bg-black/40 border border-[#23a55a]/30 flex items-center gap-3 text-left">
            <div class="w-12 h-12 rounded-xl bg-[#23a55a]/15 border border-[#23a55a]/30 flex items-center justify-center text-2xl flex-shrink-0">
              🖥️
            </div>
            <div>
              <div class="text-xs font-bold text-white flex items-center gap-1.5">
                <span>Transmissão da Tela Completa</span>
                <span class="text-[9px] text-[#23a55a] bg-[#23a55a]/20 px-1.5 py-0.5 rounded font-bold">ANTI-ECO ATIVO</span>
              </div>
              <p class="text-[11px] text-[#949ba4] mt-0.5 leading-relaxed">
                Transmite tudo na sua tela (jogos, abas e área de trabalho) sem ficar preso em nenhuma janela. O som dos amigos no Discord não ecoa!
              </p>
            </div>
          </div>

          <!-- Botão de Início -->
          <button
            @click="handleStartDesktop"
            class="w-full py-3.5 rounded-xl bg-[#23a55a] hover:bg-[#1f9350] text-white text-xs font-bold transition shadow-lg cursor-pointer active:scale-98 flex items-center justify-center gap-2"
          >
            <span>🚀 Iniciar Transmissão da Tela Inteira</span>
          </button>

          <p class="text-[10px] text-[#949ba4] text-center">
            💡 Dica: no Discord ➔ <em>Voz e Vídeo</em> ➔ coloque a <em>Saída</em> no seu fone de ouvido para isolar o áudio 100%!
          </p>
        </div>

        <!-- CENÁRIO B: Transmitir pelo Navegador -->
        <div v-else class="space-y-4">
          <div class="p-4 rounded-xl bg-[#1e1f22] border border-white/5 space-y-2 text-left">
            <h4 class="text-xs font-bold text-white">Transmissão no Navegador</h4>
            <p class="text-[11px] text-[#949ba4] leading-relaxed">
              O estúdio de transmissão será aberto no seu navegador padrão para você compartilhar sua tela ou jogo.
            </p>
          </div>

          <button
            @click="() => { emit('open-browser'); showSourceModal = false; }"
            class="w-full py-3 rounded-xl bg-[#5865F2] hover:bg-[#4752C4] text-white text-xs font-bold transition cursor-pointer shadow-md flex items-center justify-center gap-2 active:scale-98"
          >
            <span>Abrir Estúdio no Navegador</span>
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
